import Position from '../../../common/enums/position';
import IOffer from '../../../common/interfaces/offers/IOffer';
import IOffersAvailableV2Response from '../../../common/interfaces/offers/IOffersAvailableV2Response';
import IRoundInfoResponse from '../../../common/interfaces/offers/IRoundInfoResponse';

type RoundPhase = 'Acceptance' | 'Confirmation';

type RoundStatus =
    | 'idle'
    | 'loadingPhase'
    | 'loadingOffers'
    | 'active'
    | 'filtering'
    | 'prepareForTransition'
    | 'transitioning'
    | 'error';

export type RoundActions =
    | { type: 'FETCH_PHASE_START' }
    | { type: 'FETCH_PHASE_SUCCESS'; payload: IRoundInfoResponse }
    | { type: 'FETCH_OFFERS_START'; position?: Position }
    | {
          type: 'FETCH_OFFERS_SUCCESS';
          payload: IOffersAvailableV2Response;
          position?: Position;
      }
    | { type: 'FILTERING_OFFERS' }
    | { type: 'ROUND_ENDING' }
    | { type: 'ROUND_END' }
    | { type: 'ERROR'; payload: string };

interface IRoundCounters {
    effectedCount: number;
    availableCount: number;
}

interface IConfirmationOffers {
    [Position.First]: IOffer[];
    [Position.Second]: IOffer[];
    [Position.Third]: IOffer[];
}

export interface IRoundState {
    status: RoundStatus;
    currentPhase: RoundPhase | null;
    endingTime?: Date;
    confirmation2ndBeginningTime?: Date;
    confirmation3rdBeginningTime?: Date;
    counters: IRoundCounters | null;
    offers: {
        acceptance: {
            items: IOffer[];
        };
        confirmation: {
            items: IConfirmationOffers;
        };
    };
    error?: string;
}

export default function roundReducer(
    state: IRoundState,
    action: RoundActions,
): IRoundState {
    switch (action.type) {
        case 'FETCH_PHASE_START':
            return {
                status: 'loadingPhase',
                currentPhase: null,
                endingTime: undefined,
                offers: {
                    acceptance: { items: [] as IOffer[] },
                    confirmation: {
                        items: {
                            [Position.First]: [],
                            [Position.Second]: [],
                            [Position.Third]: [],
                        } as IConfirmationOffers,
                    },
                },
            } as IRoundState;

        case 'FETCH_PHASE_SUCCESS':
            return {
                ...state,
                status: 'loadingOffers',
                currentPhase: action.payload.roundPhaseInfo.phase,
                endingTime: action.payload.roundPhaseInfo.endingTime,
                confirmation2ndBeginningTime:
                    action.payload.roundPhaseInfo.confirmation2ndBeginningTime,
                confirmation3rdBeginningTime:
                    action.payload.roundPhaseInfo.confirmation3rdBeginningTime,
                counters: {
                    effectedCount: action.payload.effectedCount,
                    availableCount: action.payload.availableCount,
                },
            } as IRoundState;

        case 'FETCH_OFFERS_SUCCESS':
            return {
                ...state,
                status: 'active',
                offers:
                    state.currentPhase === 'Acceptance'
                        ? {
                              confirmation: { ...state.offers.confirmation },
                              acceptance: {
                                  items: [
                                      ...state.offers.acceptance.items,
                                      ...action.payload.data,
                                  ],
                              },
                          }
                        : {
                              acceptance: { ...state.offers.acceptance },
                              confirmation:
                                  action.position !== undefined
                                      ? {
                                            items: {
                                                ...state.offers.confirmation
                                                    .items,
                                                [action.position]: [
                                                    ...state.offers.confirmation
                                                        .items[action.position],
                                                    ...action.payload.data,
                                                ],
                                            },
                                        }
                                      : {
                                            items: {
                                                ...state.offers.confirmation
                                                    .items,
                                            },
                                        },
                          },
            } as IRoundState;

        case 'FILTERING_OFFERS':
            return {
                ...state,
                status: 'filtering',
                offers: {
                    acceptance: { items: [] as IOffer[] },
                    confirmation: {
                        items: {
                            [Position.First]: [],
                            [Position.Second]: [],
                            [Position.Third]: [],
                        } as IConfirmationOffers,
                    },
                },
            } as IRoundState;

        case 'ROUND_ENDING':
            return {
                ...state,
                status: 'prepareForTransition',
                offers: {
                    acceptance: { items: [] as IOffer[] },
                    confirmation: {
                        items: {
                            [Position.First]: [],
                            [Position.Second]: [],
                            [Position.Third]: [],
                        } as IConfirmationOffers,
                    },
                },
            } as IRoundState;

        case 'ROUND_END':
            return {
                ...state,
                status: 'transitioning',
                currentPhase: null,
            } as IRoundState;

        case 'ERROR':
            return {
                ...state,
                status: 'error',
                error: action.payload,
            } as IRoundState;

        default:
            return state;
    }
}
